import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Checkbox,
  InputAdornment,
  TextField,
  Chip,
  Alert,
  Snackbar,
  Stack,
  List,
  ListItem,
  IconButton,
  useTheme,
  useMediaQuery,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Restore as RestoreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import axios from 'axios';
import i18next, { tEnum } from '../../../lang';
import { rootStore } from '../../../stores/RootStore';
import { adminStyles } from '../../../theme/adminStyles';
import {Product, ProductSet} from "../Product/Interfaces";

type ItemState = 'inherit' | 'on' | 'off';

// ページ内アクセントカラー（タイトルバーの黄色とは別）
const ACCENT_ORANGE = '#F06922';

interface User {
  id: string;
  username: string;
  email: string;
  organizationName: string;
  level: string;
  status: string;
}

interface MappingItem {
  productType: string;
  productId: number | null;
  productSetId: number | null;
}

const ProductMappingDetail: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { viewStatusStore } = rootStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [parentOrganizationName, setParentOrganizationName] = useState<string>('');
  const [user, setUser] = useState<User | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [productSets, setProductSets] = useState<ProductSet[]>([]);
  const [childUsers, setChildUsers] = useState<User[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [filteredProductSets, setFilteredProductSets] = useState<ProductSet[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<{ [key: string]: number[] }>({});
  const [selectedProductSets, setSelectedProductSets] = useState<{ [key: string]: number[] }>({});
  const [originalSelectedProducts, setOriginalSelectedProducts] = useState<{ [key: string]: number[] }>({});
  const [originalSelectedProductSets, setOriginalSelectedProductSets] = useState<{ [key: string]: number[] }>({});
  // 상위(부모) 매핑 - override 비교용
  const [parentProducts, setParentProducts] = useState<{ [key: string]: number[] }>({});
  const [parentProductSets, setParentProductSets] = useState<{ [key: string]: number[] }>({});
  // 탭별 override 상태 (API에서 가져온 저장된 상태)
  const [tabOverrideStatus, setTabOverrideStatus] = useState<{ [key: string]: boolean }>({});
  // 하위 사용자 override 뱃지용
  const [overrideStatus, setOverrideStatus] = useState<{ [key: string]: boolean }>({});
  // 3-state 아이템 상태 (사용자가 명시적으로 변경한 항목만 저장)
  const [productItemStates, setProductItemStates] = useState<{ [targetId: string]: Record<number, ItemState> }>({});
  const [productSetItemStates, setProductSetItemStates] = useState<{ [targetId: string]: Record<number, ItemState> }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  // セクション折りたたみ状態
  const [productSectionOpen, setProductSectionOpen] = useState(true);
  const [productSetSectionOpen, setProductSetSectionOpen] = useState(true);
  // カリキュラム折りたたみ状態
  const [collapsedCurriculums, setCollapsedCurriculums] = useState<Set<string>>(new Set());

  const isClassroomManager = user?.level === 'CLASSROOM_MANAGER';
  const isBrandManager = user?.level === 'BRAND_MANAGER';
  const isCorpPartner = user?.level === 'CORP_PARTNER';
  const hasParent = isBrandManager || isClassroomManager;
  // 뷰 판별
  const isBaseView = hasParent && currentTab === 0 && !selectedChildId;
  const isMySettingsView = (hasParent && currentTab === 1 && !selectedChildId) || (!hasParent && !selectedChildId);
  const isChildView = !!selectedChildId;
  // 최상위 탭(CORP_PARTNER의 공용 탭)은 상속 개념 없음
  const isRootTab = isCorpPartner && currentTab === 0 && !selectedChildId;
  // 3-state 모드: 부모가 있는 뷰에서만 (기본 탭, 루트 탭 제외)
  const use3State = (isMySettingsView || isChildView) && !isRootTab;

  // --- 3-state 헬퍼 함수 ---
  const getItemState = (itemId: number, type: 'product' | 'set', targetId: string): ItemState => {
    const states = type === 'set' ? productSetItemStates : productItemStates;
    const explicit = states[targetId]?.[itemId];
    if (explicit !== undefined) return explicit;
    // 사용자가 변경하지 않은 항목: override 여부에 따라 기본 상태 결정
    if (!tabOverrideStatus[targetId]) return 'inherit';
    const selected = type === 'set' ? selectedProductSets : selectedProducts;
    return (selected[targetId] || []).includes(itemId) ? 'on' : 'off';
  };

  const isEffectivelyChecked = (itemId: number, type: 'product' | 'set', targetId: string): boolean => {
    const state = getItemState(itemId, type, targetId);
    if (state === 'on') return true;
    if (state === 'off') return false;
    // inherit: 부모 설정 따름
    const parent = type === 'set' ? parentProductSets : parentProducts;
    return (parent[targetId] || []).includes(itemId);
  };

  const getResolvedProductIds = (targetId: string): number[] => {
    return products.filter(p => isEffectivelyChecked(p.id, 'product', targetId)).map(p => p.id);
  };

  const getResolvedProductSetIds = (targetId: string): number[] => {
    return productSets.filter(ps => isEffectivelyChecked(ps.id, 'set', targetId)).map(ps => ps.id);
  };

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, []);

  useEffect(() => {
    filterProducts();
    filterProductSets();
  }, [products, productSets, searchKeyword]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/users/${userId}`);
      const userData = response.data;
      setUser(userData);

      // hasParent(BRAND_MANAGER, CLASSROOM_MANAGER)는 My 탭(1)을 초기 활성으로
      if (userData.level === 'BRAND_MANAGER' || userData.level === 'CLASSROOM_MANAGER') {
        setCurrentTab(1);
      }

      if (userData.level !== 'CLASSROOM_MANAGER') {
        await fetchChildUsers();
      }
      await fetchProducts();
      await fetchProductSets();

      await fetchProductMappings(null);

      if (userData.level !== 'CLASSROOM_MANAGER') {
        await fetchOverrideStatus();
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/v1/admin/products');
      setProducts(response.data.filter((product: Product) => product.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchProductSets = async () => {
    try {
      const response = await axios.get('/api/v1/admin/product-sets');
      setProductSets(response.data);
    } catch (error) {
      console.error('Failed to fetch product sets:', error);
    }
  };

  const fetchChildUsers = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/users/${userId}/childrens`);
      setChildUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch child users:', error);
    }
  };

  const fetchOverrideStatus = async () => {
    try {
      const response = await axios.get(`/api/v1/admin/product-mappings/${userId}/override-status`);
      setOverrideStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch override status:', error);
    }
  };

  const extractIds = (mappings: MappingItem[]) => {
    const productIds: number[] = [];
    const productSetIds: number[] = [];
    for (const mapping of mappings) {
      if (mapping.productType === 'normal' && mapping.productId !== null) {
        productIds.push(mapping.productId);
      } else if (mapping.productType === 'set' && mapping.productSetId !== null) {
        productSetIds.push(mapping.productSetId);
      }
    }
    return { productIds, productSetIds };
  };

  const fetchProductMappings = async (childUserId: string | null) => {
    try {
      let url = `/api/v1/admin/product-mappings/${userId}`;
      if (childUserId !== null) {
        url += `?childUserId=${childUserId}`;
      }

      const response = await axios.get(url);
      const data = response.data;

      // 유효 매핑 (자체 또는 계승)
      const { productIds, productSetIds } = extractIds(data.mappings || []);
      // 상위 매핑 (비교용)
      const parent = extractIds(data.parentMappings || []);

      const targetUserId = childUserId || 'common';
      setSelectedProducts(prev => ({ ...prev, [targetUserId]: productIds }));
      setSelectedProductSets(prev => ({ ...prev, [targetUserId]: productSetIds }));
      setOriginalSelectedProducts(prev => ({ ...prev, [targetUserId]: [...productIds] }));
      setOriginalSelectedProductSets(prev => ({ ...prev, [targetUserId]: [...productSetIds] }));
      setParentProducts(prev => ({ ...prev, [targetUserId]: parent.productIds }));
      setParentProductSets(prev => ({ ...prev, [targetUserId]: parent.productSetIds }));
      setTabOverrideStatus(prev => ({ ...prev, [targetUserId]: data.overridden || false }));
      // 3-state 아이템 상태 초기화
      setProductItemStates(prev => ({ ...prev, [targetUserId]: {} }));
      setProductSetItemStates(prev => ({ ...prev, [targetUserId]: {} }));
      if (data.parentOrganizationName) {
        setParentOrganizationName(data.parentOrganizationName);
      }
    } catch (error) {
      console.error('Failed to fetch product mappings:', error);
    }
  };

  const filterProducts = () => {
    let filtered = products;
    if (searchKeyword.trim()) {
      filtered = products.filter(product =>
        (product.title || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (product.curriculumName || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
        (product.volume || '').toLowerCase().includes(searchKeyword.toLowerCase())
      );
    }
    setFilteredProducts(filtered);
  };

  const filterProductSets = () => {
    let filtered = productSets;
    if (searchKeyword.trim()) {
      filtered = productSets.filter(productSet =>
          (productSet.name || '').toLowerCase().includes(searchKeyword.toLowerCase())
      );
    }
    setFilteredProductSets(filtered);
  };

  // 2-state 토글 (루트 탭 전용)
  const handleProductToggle = (productId: number, targetUserId: string) => {
    setSelectedProducts(prev => {
      const current = prev[targetUserId] || [];
      const updated = current.includes(productId)
        ? current.filter(id => id !== productId)
        : [...current, productId];
      return { ...prev, [targetUserId]: updated };
    });
  };

  const handleProductSetToggle = (productSetId: number, targetUserId: string) => {
    setSelectedProductSets(prev => {
      const current = prev[targetUserId] || [];
      const updated = current.includes(productSetId)
        ? current.filter(id => id !== productSetId)
        : [...current, productSetId];
      return { ...prev, [targetUserId]: updated };
    });
  };

  // on/off トグル
  const handleItemToggle = (itemId: number, type: 'product' | 'set', targetId: string) => {
    const effectiveChecked = isEffectivelyChecked(itemId, type, targetId);
    const nextState: ItemState = effectiveChecked ? 'off' : 'on';

    const setter = type === 'set' ? setProductSetItemStates : setProductItemStates;
    setter(prev => ({
      ...prev,
      [targetId]: { ...(prev[targetId] || {}), [itemId]: nextState }
    }));
  };

  const handleSelectAll = (targetUserId: string, select: boolean) => {
    if (use3State) {
      // 3-state: 모든 표시 항목을 명시적 on/off로 설정
      const pStates: Record<number, ItemState> = {};
      filteredProducts.forEach(p => { pStates[p.id] = select ? 'on' : 'off'; });
      setProductItemStates(prev => ({
        ...prev,
        [targetUserId]: { ...(prev[targetUserId] || {}), ...pStates }
      }));

      const psStates: Record<number, ItemState> = {};
      filteredProductSets.forEach(ps => { psStates[ps.id] = select ? 'on' : 'off'; });
      setProductSetItemStates(prev => ({
        ...prev,
        [targetUserId]: { ...(prev[targetUserId] || {}), ...psStates }
      }));
    } else {
      setSelectedProducts(prev => ({
        ...prev,
        [targetUserId]: select ? filteredProducts.map(p => p.id) : []
      }));
      setSelectedProductSets(prev => ({
        ...prev,
        [targetUserId]: select ? filteredProductSets.map(p => p.id) : []
      }));
    }
  };

  const handleSave = async () => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });
      const targetUserId = getCurrentTargetUserId();
      const childUserId: string | null = selectedChildId;

      const mappings: Array<{ productType: string; productId: number }> = [];

      if (use3State) {
        getResolvedProductIds(targetUserId).forEach(productId => {
          mappings.push({ productType: 'normal', productId });
        });
        getResolvedProductSetIds(targetUserId).forEach(productSetId => {
          mappings.push({ productType: 'set', productId: productSetId });
        });
      } else {
        (selectedProducts[targetUserId] || []).forEach(productId => {
          mappings.push({ productType: 'normal', productId });
        });
        (selectedProductSets[targetUserId] || []).forEach(productSetId => {
          mappings.push({ productType: 'set', productId: productSetId });
        });
      }

      await axios.post(`/api/v1/admin/product-mappings/${userId}`, {
        childUserId,
        mappings,
      });

      // 성공 후 원본 업데이트
      if (use3State) {
        const resolvedProds = getResolvedProductIds(targetUserId);
        const resolvedSets = getResolvedProductSetIds(targetUserId);
        setSelectedProducts(prev => ({ ...prev, [targetUserId]: resolvedProds }));
        setSelectedProductSets(prev => ({ ...prev, [targetUserId]: resolvedSets }));
        setOriginalSelectedProducts(prev => ({ ...prev, [targetUserId]: [...resolvedProds] }));
        setOriginalSelectedProductSets(prev => ({ ...prev, [targetUserId]: [...resolvedSets] }));
        // 저장 후 아이템 상태 초기화 (모두 explicit)
        setProductItemStates(prev => ({ ...prev, [targetUserId]: {} }));
        setProductSetItemStates(prev => ({ ...prev, [targetUserId]: {} }));
      } else {
        setOriginalSelectedProducts(prev => ({
          ...prev,
          [targetUserId]: [...(selectedProducts[targetUserId] || [])]
        }));
        setOriginalSelectedProductSets(prev => ({
          ...prev,
          [targetUserId]: [...(selectedProductSets[targetUserId] || [])]
        }));
      }

      // override 상태를 true로 갱신 (저장하면 override가 됨)
      setTabOverrideStatus(prev => ({ ...prev, [targetUserId]: true }));

      if (!isClassroomManager) {
        await fetchOverrideStatus();
      }

      setSnackbar({
        open: true,
        message: i18next.t('product_mapping.save_success'),
        severity: 'success'
      });

    } catch (error: any) {
      console.error('Failed to save product mappings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || i18next.t('product_mapping.save_error'),
        severity: 'error'
      });
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleResetToInherited = async () => {
    try {
      viewStatusStore.setLoadingStatus({ open: true });

      const childUserId: string | null = selectedChildId;

      const params = childUserId ? `?childUserId=${childUserId}` : '';
      await axios.delete(`/api/v1/admin/product-mappings/${userId}/override${params}`);

      // 다시 로드 (이제 계승 데이터가 표시됨 + 아이템 상태 초기화)
      await fetchProductMappings(childUserId);

      if (!isClassroomManager) {
        await fetchOverrideStatus();
      }

      setSnackbar({
        open: true,
        message: i18next.t('product_mapping.restored_to_parent'),
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || i18next.t('product_mapping.save_error'),
        severity: 'error'
      });
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSelectedChildId(null);

    try {
      viewStatusStore.setLoadingStatus({ open: true });
      setSearchKeyword('');
      // 탭 변경 시 common 데이터 다시 로드 (아이템 상태도 초기화됨)
      await fetchProductMappings(null);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const handleChildSelect = async (childId: string | '') => {
    if (childId === '') {
      // 드롭다운 리셋
      setSelectedChildId(null);
      return;
    }
    setSelectedChildId(childId);

    try {
      viewStatusStore.setLoadingStatus({ open: true });
      setSearchKeyword('');
      await fetchProductMappings(childId);
    } finally {
      viewStatusStore.setLoadingStatus({ open: false });
    }
  };

  const getCurrentTargetUserId = () => {
    if (selectedChildId) return selectedChildId;
    return 'common';
  };

  const hasChanges = () => {
    const targetUserId = getCurrentTargetUserId();

    if (use3State) {
      const resolvedProds = getResolvedProductIds(targetUserId).sort((a, b) => a - b);
      const originalProds = [...(originalSelectedProducts[targetUserId] || [])].sort((a, b) => a - b);
      const resolvedSets = getResolvedProductSetIds(targetUserId).sort((a, b) => a - b);
      const originalSets = [...(originalSelectedProductSets[targetUserId] || [])].sort((a, b) => a - b);

      if (resolvedProds.length !== originalProds.length || resolvedSets.length !== originalSets.length) return true;
      for (let i = 0; i < resolvedProds.length; i++) {
        if (resolvedProds[i] !== originalProds[i]) return true;
      }
      for (let i = 0; i < resolvedSets.length; i++) {
        if (resolvedSets[i] !== originalSets[i]) return true;
      }
      return false;
    }

    const currentProds = [...(selectedProducts[targetUserId] || [])].sort((a, b) => a - b);
    const originalProds = [...(originalSelectedProducts[targetUserId] || [])].sort((a, b) => a - b);
    const currentSets = [...(selectedProductSets[targetUserId] || [])].sort((a, b) => a - b);
    const originalSets = [...(originalSelectedProductSets[targetUserId] || [])].sort((a, b) => a - b);

    if (currentProds.length !== originalProds.length || currentSets.length !== originalSets.length) return true;
    for (let i = 0; i < currentProds.length; i++) {
      if (currentProds[i] !== originalProds[i]) return true;
    }
    for (let i = 0; i < currentSets.length; i++) {
      if (currentSets[i] !== originalSets[i]) return true;
    }
    return false;
  };

  /**
   * 현재 선택 상태가 상위 매핑과 다른지 확인 (실시간 override 감지)
   */
  const isCurrentDifferentFromParent = () => {
    const targetUserId = getCurrentTargetUserId();

    if (use3State) {
      const resolvedProds = getResolvedProductIds(targetUserId).sort((a, b) => a - b);
      const parentProds = [...(parentProducts[targetUserId] || [])].sort((a, b) => a - b);
      const resolvedSets = getResolvedProductSetIds(targetUserId).sort((a, b) => a - b);
      const parentSets = [...(parentProductSets[targetUserId] || [])].sort((a, b) => a - b);

      if (resolvedProds.length !== parentProds.length || resolvedSets.length !== parentSets.length) return true;
      for (let i = 0; i < resolvedProds.length; i++) if (resolvedProds[i] !== parentProds[i]) return true;
      for (let i = 0; i < resolvedSets.length; i++) if (resolvedSets[i] !== parentSets[i]) return true;
      return false;
    }

    const currentProds = [...(selectedProducts[targetUserId] || [])].sort((a, b) => a - b);
    const parentProds = [...(parentProducts[targetUserId] || [])].sort((a, b) => a - b);
    const currentSets = [...(selectedProductSets[targetUserId] || [])].sort((a, b) => a - b);
    const parentSets = [...(parentProductSets[targetUserId] || [])].sort((a, b) => a - b);

    if (currentProds.length !== parentProds.length || currentSets.length !== parentSets.length) return true;
    for (let i = 0; i < currentProds.length; i++) {
      if (currentProds[i] !== parentProds[i]) return true;
    }
    for (let i = 0; i < currentSets.length; i++) {
      if (currentSets[i] !== parentSets[i]) return true;
    }
    return false;
  };

  const getOverrideLabel = () => {
    if (isChildView) {
      if (isCorpPartner) return i18next.t('product_mapping.brand_custom_set');
      if (isBrandManager) return i18next.t('product_mapping.classroom_custom_set');
    }
    if (isBrandManager) return i18next.t('product_mapping.brand_custom_set');
    if (isClassroomManager) return i18next.t('product_mapping.classroom_custom_set');
    return i18next.t('product_mapping.brand_custom_set');
  };

  const getTabLabel = (child: User) => {
    const label = `${child.id}(${child.organizationName})`;
    if (overrideStatus[child.id]) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          <Chip label={i18next.t('product_mapping.custom_set')} size="small" sx={{ height: '20px', fontSize: '0.7rem', bgcolor: ACCENT_ORANGE, color: '#fff' }} />
        </Box>
      );
    }
    return label;
  };

  const showOverrideFeatures = !isRootTab && !isBaseView;
  const targetUserId = getCurrentTargetUserId();
  const savedOverridden = tabOverrideStatus[targetUserId] || false;
  const currentlyDifferent = showOverrideFeatures ? isCurrentDifferentFromParent() : false;

  const getParentNameForDiff = () => {
    if (isChildView) return user?.organizationName || '';
    if (isMySettingsView) return parentOrganizationName;
    return '';
  };

  // 선택된 상품 수 계산
  const getEffectiveProductCount = () => {
    const tid = getCurrentTargetUserId();
    if (isBaseView) return parentProducts['common']?.length || 0;
    if (use3State) return getResolvedProductIds(tid).length;
    return selectedProducts[tid]?.length || 0;
  };
  const getEffectiveProductSetCount = () => {
    const tid = getCurrentTargetUserId();
    if (isBaseView) return parentProductSets['common']?.length || 0;
    if (use3State) return getResolvedProductSetIds(tid).length;
    return selectedProductSets[tid]?.length || 0;
  };

  // カリキュラム名で商品をグループ化
  const groupedProducts = useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    filteredProducts.forEach(product => {
      const key = product.curriculumName || '未分類';
      if (!groups[key]) groups[key] = [];
      groups[key].push(product);
    });
    return groups;
  }, [filteredProducts]);

  const toggleCurriculumCollapse = (curriculumName: string) => {
    setCollapsedCurriculums(prev => {
      const next = new Set(prev);
      if (next.has(curriculumName)) next.delete(curriculumName);
      else next.add(curriculumName);
      return next;
    });
  };

  const handleExpandAllCurriculums = () => setCollapsedCurriculums(new Set());
  const handleCollapseAllCurriculums = () => setCollapsedCurriculums(new Set(Object.keys(groupedProducts)));

  const getCurriculumCheckState = (productIds: number[]): { allChecked: boolean; indeterminate: boolean } => {
    const tid = getCurrentTargetUserId();
    let checkedCount: number;
    if (isBaseView) {
      checkedCount = productIds.filter(id => (parentProducts['common'] || []).includes(id)).length;
    } else if (use3State) {
      checkedCount = productIds.filter(id => isEffectivelyChecked(id, 'product', tid)).length;
    } else {
      checkedCount = productIds.filter(id => (selectedProducts[tid] || []).includes(id)).length;
    }
    return {
      allChecked: checkedCount === productIds.length && productIds.length > 0,
      indeterminate: checkedCount > 0 && checkedCount < productIds.length,
    };
  };

  const handleCurriculumCheck = (productIds: number[]) => {
    const tid = getCurrentTargetUserId();
    const { allChecked } = getCurriculumCheckState(productIds);
    const newState = !allChecked;
    if (use3State) {
      const pStates: Record<number, ItemState> = {};
      productIds.forEach(id => { pStates[id] = newState ? 'on' : 'off'; });
      setProductItemStates(prev => ({
        ...prev,
        [tid]: { ...(prev[tid] || {}), ...pStates }
      }));
    } else {
      setSelectedProducts(prev => {
        const current = prev[tid] || [];
        if (newState) {
          const newIds = productIds.filter(id => !current.includes(id));
          return { ...prev, [tid]: [...current, ...newIds] };
        } else {
          return { ...prev, [tid]: current.filter(id => !productIds.includes(id)) };
        }
      });
    }
  };

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* ヘッダー */}
      <Box sx={adminStyles.headerBox}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/admin/product-mappings')}
            sx={{ bgcolor: '#f5f5f7', '&:hover': { bgcolor: '#e8e8e8' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {user?.id} ({user?.organizationName})
          </Typography>
          {user && (
            <Chip
              label={tEnum(user.level)}
              color={user.level === 'CORP_PARTNER' ? 'primary' : user.level === 'BRAND_MANAGER' ? 'info' : 'secondary'}
              size="small"
            />
          )}
        </Box>
        {!isBaseView && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
            {/* 계승으로 복원 버튼 (override된 탭에서만 표시) */}
            {showOverrideFeatures && savedOverridden && (
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={handleResetToInherited}
                sx={{ ...adminStyles.actionButton, borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE, '&:hover': { borderColor: ACCENT_ORANGE, bgcolor: 'rgba(240, 105, 34, 0.08)' } }}
              >
                {i18next.t('product_mapping.restore_parent')}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => handleSelectAll(getCurrentTargetUserId(), true)}
              sx={{ ...adminStyles.actionButton, borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE, '&:hover': { borderColor: ACCENT_ORANGE, bgcolor: 'rgba(240, 105, 34, 0.08)' } }}
            >
              {i18next.t('common.select_all')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSelectAll(getCurrentTargetUserId(), false)}
              sx={{ ...adminStyles.actionButton, borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE, '&:hover': { borderColor: ACCENT_ORANGE, bgcolor: 'rgba(240, 105, 34, 0.08)' } }}
            >
              {i18next.t('common.deselect_all')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges()}
              sx={{ ...adminStyles.actionButton, bgcolor: ACCENT_ORANGE, color: '#fff', '&:hover': { bgcolor: '#D85A1B' } }}
            >
              {i18next.t('common.save')}
            </Button>
          </Stack>
        )}
      </Box>

      {/* タブ + ドロップダウン (breadcrumb style with >) */}
      <Paper sx={{ borderRadius: '12px', mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', px: 1 }}>
        {hasParent ? (
          <>
            {/* 기본(부모) 탭 */}
            <Typography
              onClick={(e) => handleTabChange(e, 0)}
              sx={{
                px: 2, py: 1.5, cursor: 'pointer', fontSize: '0.875rem', userSelect: 'none',
                fontWeight: !selectedChildId && currentTab === 0 ? 600 : 400,
                color: !selectedChildId && currentTab === 0 ? ACCENT_ORANGE : 'text.secondary',
                borderBottom: '2px solid',
                borderBottomColor: !selectedChildId && currentTab === 0 ? ACCENT_ORANGE : 'transparent',
                '&:hover': { color: ACCENT_ORANGE, bgcolor: 'action.hover' },
              }}
            >
              {`${i18next.t('common.default')}(${parentOrganizationName})`}
            </Typography>
            <Typography sx={{ color: 'text.disabled', fontSize: '0.875rem', flexShrink: 0 }}>&gt;</Typography>
            {/* My(자신) 탭 */}
            <Typography
              onClick={(e) => handleTabChange(e, 1)}
              sx={{
                px: 2, py: 1.5, cursor: 'pointer', fontSize: '0.875rem', userSelect: 'none',
                fontWeight: !selectedChildId && currentTab === 1 ? 600 : 400,
                color: !selectedChildId && currentTab === 1 ? ACCENT_ORANGE : 'text.secondary',
                borderBottom: '2px solid',
                borderBottomColor: !selectedChildId && currentTab === 1 ? ACCENT_ORANGE : 'transparent',
                '&:hover': { color: ACCENT_ORANGE, bgcolor: 'action.hover' },
              }}
            >
              {`My(${user?.organizationName || ''})`}
            </Typography>
          </>
        ) : (
          /* CORP_PARTNER: My(조직명) 탭 */
          <Typography
            onClick={(e) => handleTabChange(e, 0)}
            sx={{
              px: 2, py: 1.5, cursor: 'pointer', fontSize: '0.875rem', userSelect: 'none',
              fontWeight: !selectedChildId && currentTab === 0 ? 600 : 400,
              color: !selectedChildId && currentTab === 0 ? ACCENT_ORANGE : 'text.secondary',
              borderBottom: '2px solid',
              borderBottomColor: !selectedChildId && currentTab === 0 ? ACCENT_ORANGE : 'transparent',
              '&:hover': { color: ACCENT_ORANGE, bgcolor: 'action.hover' },
            }}
          >
            {`My(${user?.organizationName || ''})`}
          </Typography>
        )}
        {/* 하위 사용자 드롭다운 (CLASSROOM_MANAGER 제외) */}
        {!isClassroomManager && childUsers.length > 0 && (
          <>
            <Typography sx={{ mx: 0.5, color: 'text.disabled', fontSize: '0.875rem', flexShrink: 0 }}>&gt;</Typography>
            <FormControl size="small" sx={{ minWidth: 200, my: 1, mr: 1 }}>
              <InputLabel sx={{ fontSize: '0.875rem' }}>{i18next.t('user.select_sub_user')}</InputLabel>
              <Select
                value={selectedChildId || ''}
                label={i18next.t('user.select_sub_user')}
                onChange={(e) => handleChildSelect(e.target.value as string)}
                sx={{ fontSize: '0.875rem' }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>-</MenuItem>
                {childUsers.map((child) => (
                  <MenuItem key={child.id} value={child.id} sx={{ fontSize: '0.875rem' }}>
                    {child.id}({child.organizationName})
                    {overrideStatus[child.id] && (
                      <Chip label={i18next.t('product_mapping.custom_set')} size="small" sx={{ ml: 1, height: '20px', fontSize: '0.7rem', bgcolor: ACCENT_ORANGE, color: '#fff' }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Paper>

      {/* Override 상태 표시 */}
      {showOverrideFeatures && (
        <Alert
          severity={currentlyDifferent ? 'warning' : 'info'}
          sx={{ mb: 2 }}
        >
          {currentlyDifferent ? (
            <Typography variant="body2">
              {getOverrideLabel()}
              {hasChanges() && !savedOverridden && ` - ${i18next.t('product_mapping.save_creates_custom')}`}
            </Typography>
          ) : (
            <Typography variant="body2">
              {i18next.t('product_mapping.inheriting')} - {i18next.t('product_mapping.same_as_parent')}
            </Typography>
          )}
        </Alert>
      )}

      {/* 検索 */}
      <Paper sx={adminStyles.filterPaper}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder={i18next.t('product_mapping.search_by_curriculum')}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchKeyword && (
                <IconButton size="small" onClick={() => setSearchKeyword('')}>
                  <ClearIcon />
                </IconButton>
              ),
            }}
          />
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {i18next.t('product_mapping.help_guide')}
        </Typography>
      </Alert>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {i18next.t('product_mapping.selected_products')}: {getEffectiveProductCount()}
        </Typography>
        <Typography variant="body2">
          {i18next.t('product_set.selected_set_products')}: {getEffectiveProductSetCount()}
        </Typography>
      </Alert>

      {/* 商品一覧（折りたたみ対応） */}
      <Card>
        <CardHeader
          title={i18next.t('product.product_list')}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={i18next.t('common.expand_all')}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleExpandAllCurriculums(); }}
                >
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18next.t('common.collapse_all')}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleCollapseAllCurriculums(); }}
                >
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small">
                {productSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
          onClick={() => setProductSectionOpen(!productSectionOpen)}
          sx={{ cursor: 'pointer' }}
        />
        <Collapse in={productSectionOpen}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <List dense disablePadding>
              {Object.entries(groupedProducts).map(([curriculumName, currProducts]) => {
                const currProductIds = currProducts.map(p => p.id);
                const { allChecked, indeterminate } = getCurriculumCheckState(currProductIds);
                const isCollapsed = collapsedCurriculums.has(curriculumName);

                return (
                  <React.Fragment key={curriculumName}>
                    {/* カリキュラムグループヘッダー */}
                    <ListItem
                      divider
                      sx={{ py: '4px', px: 2, minHeight: '40px', bgcolor: 'grey.50' }}
                    >
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onChange={() => handleCurriculumCheck(currProductIds)}
                        disabled={isBaseView}
                        sx={{ p: '4px', mr: 0.5 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => toggleCurriculumCollapse(curriculumName)}
                        sx={{ mr: 0.5, p: '4px' }}
                      >
                        {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </IconButton>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {curriculumName}
                      </Typography>
                    </ListItem>
                    {/* カリキュラム内商品リスト */}
                    <Collapse in={!isCollapsed}>
                      {currProducts.map((product) => {
                        const tid = getCurrentTargetUserId();

                        if (isBaseView) {
                          const isSelected = (parentProducts['common'] || []).includes(product.id);
                          return (
                            <ListItem key={product.id} divider sx={{ py: '4px', px: 2, pl: 6, minHeight: '40px' }}>
                              <Checkbox size="small" checked={isSelected} disabled sx={{ p: '4px', mr: 1 }} />
                              <Typography variant="body2" sx={{ mr: 1 }}>{`${product.volume || ''} ${product.title || ''}`}</Typography>
                              <Chip label={`${product.price.toLocaleString()} ${i18next.t('common.yen')}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }} />
                            </ListItem>
                          );
                        }

                        if (use3State) {
                          const effectiveChecked = isEffectivelyChecked(product.id, 'product', tid);
                          const parentOn = (parentProducts[tid] || []).includes(product.id);
                          const isDiffFromParent = effectiveChecked !== parentOn;

                          return (
                            <ListItem
                              key={product.id}
                              divider
                              sx={{ py: '4px', px: 2, pl: 6, minHeight: '40px' }}
                            >
                              <Checkbox
                                size="small"
                                checked={effectiveChecked}
                                onChange={() => handleItemToggle(product.id, 'product', tid)}
                                sx={{ p: '4px', mr: 1 }}
                              />
                              <Typography variant="body2" sx={{ mr: 1 }}>
                                {`${product.volume || ''} ${product.title || ''}`}
                              </Typography>
                              <Chip
                                label={`${product.price.toLocaleString()} ${i18next.t('common.yen')}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }}
                              />
                              {isDiffFromParent && (
                                <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap', color: ACCENT_ORANGE }}>
                                  ({i18next.t('user.this_item_differs', { name: getParentNameForDiff() })})
                                </Typography>
                              )}
                            </ListItem>
                          );
                        }

                        const isSelected = selectedProducts[tid]?.includes(product.id) || false;
                        return (
                          <ListItem key={product.id} divider sx={{ py: '4px', px: 2, pl: 6, minHeight: '40px' }}>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => handleProductToggle(product.id, tid)}
                              sx={{ p: '4px', mr: 1 }}
                            />
                            <Typography variant="body2" sx={{ mr: 1 }}>{`${product.volume || ''} ${product.title || ''}`}</Typography>
                            <Chip label={`${product.price.toLocaleString()} ${i18next.t('common.yen')}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }} />
                          </ListItem>
                        );
                      })}
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
            {filteredProducts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {searchKeyword ? i18next.t('common.no_search_results') : i18next.t('product.no_products')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* セット商品一覧（折りたたみ対応） */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title={i18next.t('product_set.set_product_list')}
          action={
            <IconButton size="small">
              {productSetSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          onClick={() => setProductSetSectionOpen(!productSetSectionOpen)}
          sx={{ cursor: 'pointer' }}
        />
        <Collapse in={productSetSectionOpen}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <List dense disablePadding>
            {filteredProductSets.map((productSet) => {
              const tid = getCurrentTargetUserId();

              if (isBaseView) {
                const isSelected = (parentProductSets['common'] || []).includes(productSet.id);
                return (
                  <ListItem key={productSet.id} divider sx={{ py: '4px', px: 2, minHeight: '40px' }}>
                    <Checkbox size="small" checked={isSelected} disabled sx={{ p: '4px', mr: 1 }} />
                    <Typography variant="body2" sx={{ mr: 1 }}>{productSet.name}</Typography>
                    <Chip label={`${productSet.price.toLocaleString()} ${i18next.t('common.yen')}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }} />
                  </ListItem>
                );
              }

              if (use3State) {
                const effectiveChecked = isEffectivelyChecked(productSet.id, 'set', tid);
                const parentOn = (parentProductSets[tid] || []).includes(productSet.id);
                const isDiffFromParent = effectiveChecked !== parentOn;

                return (
                  <ListItem
                    key={productSet.id}
                    divider
                    sx={{ py: '4px', px: 2, minHeight: '40px' }}
                  >
                    <Checkbox
                      size="small"
                      checked={effectiveChecked}
                      onChange={() => handleItemToggle(productSet.id, 'set', tid)}
                      sx={{ p: '4px', mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {productSet.name}
                    </Typography>
                    <Chip
                      label={`${productSet.price.toLocaleString()} ${i18next.t('common.yen')}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }}
                    />
                    {isDiffFromParent && (
                      <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap', color: ACCENT_ORANGE }}>
                        ({i18next.t('user.this_item_differs', { name: getParentNameForDiff() })})
                      </Typography>
                    )}
                  </ListItem>
                );
              }

              const isSelected = selectedProductSets[tid]?.includes(productSet.id) || false;
              return (
                <ListItem key={productSet.id} divider sx={{ py: '4px', px: 2, minHeight: '40px' }}>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => handleProductSetToggle(productSet.id, tid)}
                    sx={{ p: '4px', mr: 1 }}
                  />
                  <Typography variant="body2" sx={{ mr: 1 }}>{productSet.name}</Typography>
                  <Chip label={`${productSet.price.toLocaleString()} ${i18next.t('common.yen')}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: '22px', borderColor: ACCENT_ORANGE, color: ACCENT_ORANGE }} />
                </ListItem>
              );
            })}
          </List>
          {filteredProductSets.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {searchKeyword ? i18next.t('common.no_search_results') : i18next.t('product_set.no_set_products')}
              </Typography>
            </Box>
          )}
        </CardContent>
        </Collapse>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductMappingDetail;
